import React from "react";
import SendIcon from "@mui/icons-material/Send";
import CircularProgress from "@mui/icons-material/RotateRight";
import AssistantIcon from "@mui/icons-material/Assistant";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { API_get_logbook_ask } from "../../../constants";
import { postRightworkAPI } from "../requests/utilities/requests";

// Mirrors the backend's MAX_QUESTION_CHARS: the server answers only the
// first 500 characters, so the input must not accept text the answer would
// silently ignore.
const MAX_QUESTION_CHARS = 500;

const WELCOME_TEXT =
  "Hi! I'm your logbook assistant. Ask me about this store's logbook history and I'll answer only from entries your team actually wrote, with the entries I used listed under each answer.\n\nHere are some things you can ask:\n- \"When did we last service the fryer?\"\n- \"What issues came up with the walk-in this summer?\"\n- \"Who worked the night the POS went down?\"\n- \"Summarize last week's business summaries\"";

// Ask the logbook: a natural-language question over this store's entry
// history, answered strictly from retrieved entries (the backend refuses to
// answer from model memory). Sources render under the answer so the manager
// can see exactly which entries it stands on. Reached via the "Chat with
// logbook" button
// on the hub's manager-level Reports tab;
// the backend enforces UserIsStoreManager regardless.
export default function LogbookAskDisplay(props) {
  const [question, setQuestion] = React.useState("");
  const [status, setStatus] = React.useState("idle"); // idle | loading | done
  const [exchanges, setExchanges] = React.useState([]); // {question, text, sources}
  const [available, setAvailable] = React.useState(true);
  const [pending, setPending] = React.useState(null); // {question, askedAt} while loading
  const messagesEndRef = React.useRef(null);
  const textareaRef = React.useRef(null);

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [exchanges, pending, status]);

  React.useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [question]);

  // Answers and follow-up context are per-store: a multi-store manager
  // switching stores must not carry Store A's Q/A into Store B's
  // conversation history (the model would blend A's answers into a
  // supposedly B-grounded response). A monotonic GENERATION guards the
  // in-flight case - store identity alone would readmit a stale request
  // after an A->B->A switch. Every ask and every store switch advances
  // the generation; only a response from the current generation may
  // write state.
  const askGenerationRef = React.useRef(0);
  React.useEffect(() => {
    askGenerationRef.current += 1;
    setExchanges([]);
    setPending(null);
    setQuestion("");
    setStatus("idle");
    setAvailable(true);
  }, [props.homeStore]);

  const ask = async () => {
    const trimmed = question.trim();
    if (!trimmed || status === "loading" || !available) {
      return;
    }
    setStatus("loading");
    // Clear on SUBMIT, not on response: the field stays editable while the
    // answer loads, and anything typed in the meantime is the manager's
    // next question - a response-time reset would erase that draft.
    setQuestion("");
    const askedAt = new Date();
    setPending({ question: trimmed, askedAt });
    const requestGeneration = ++askGenerationRef.current;
    const isCurrent = () => askGenerationRef.current === requestGeneration;
    try {
      // Recent turns ride along so follow-ups ("what about the week
      // before?") stay coherent; the backend keeps only user/assistant
      // roles.
      const conversation_history = exchanges
        .slice(-3)
        .flatMap((exchange) => [
          { role: "user", content: exchange.question },
          { role: "assistant", content: exchange.text },
        ]);
      const res = await postRightworkAPI(API_get_logbook_ask, {
        store_id: props.homeStore,
        question: trimmed,
        conversation_history: JSON.stringify(conversation_history),
      });
      const payload = res?.data?.redux?.payload?.response || {};
      if (!isCurrent()) {
        return;
      }
      if (payload.available === false) {
        setAvailable(false);
        setStatus("done");
        return;
      }
      setExchanges((prev) => [
        ...prev,
        {
          question: trimmed,
          askedAt,
          answeredAt: new Date(),
          text: payload.text || "No answer came back. Try again.",
          sources: payload.sources || [],
        },
      ]);
    } catch (error) {
      if (isCurrent()) {
        setExchanges((prev) => [
          ...prev,
          {
            question: trimmed,
            askedAt,
            answeredAt: new Date(),
            text: "The logbook search hit a problem answering that. Try again in a moment.",
            sources: [],
          },
        ]);
      }
    } finally {
      if (isCurrent()) {
        setStatus("done");
        setPending(null);
      }
    }
  };

  const resetConversation = () => {
    askGenerationRef.current += 1;
    setExchanges([]);
    setPending(null);
    setQuestion("");
    setStatus("idle");
  };

  const formatTimestamp = (timestamp) =>
    timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  const canSend = available && question.trim() && status !== "loading";

  return (
    <div className="flex h-screen bg-gray-50">
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="bg-white border-b px-4 py-3 flex items-center justify-between shadow-sm">
          <div className="flex items-center min-w-0">
            {props.onBack && (
              <>
                <button
                  onClick={props.onBack}
                  title={`Back to ${props.backLabel || "Reports"}`}
                  className="flex items-center gap-1.5 -ml-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium text-gray-500 hover:text-violet-700 hover:bg-gray-100 transition-colors"
                >
                  <ArrowBackIcon style={{ fontSize: 18 }} />
                  <span>{props.backLabel || "Reports"}</span>
                </button>
                <div className="h-6 w-px bg-gray-200 mx-3 flex-shrink-0" />
              </>
            )}
            <div className="w-9 h-9 rounded-full bg-violet-600 flex items-center justify-center flex-shrink-0">
              <AssistantIcon fontSize="small" className="text-white" />
            </div>
            <div className="ml-3 min-w-0">
              <h2 className="font-medium text-gray-800">Ask the Logbook</h2>
              <p className="text-xs text-gray-500 truncate">
                Answers come only from this store&apos;s logbook entries
              </p>
            </div>
          </div>
          <button
            onClick={resetConversation}
            className="text-sm text-gray-500 hover:text-violet-600"
          >
            Reset
          </button>
        </div>

        {/* Messages area */}
        <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
          <div className="mb-4 flex justify-start">
            <div
              className="rounded-lg px-4 py-3 bg-white border border-gray-200 text-gray-700 whitespace-pre-line text-sm"
              style={{ maxWidth: "75%" }}
            >
              {available
                ? WELCOME_TEXT
                : "Ask the Logbook isn't available for this company."}
            </div>
          </div>

          {exchanges.map((exchange, index) => (
            <React.Fragment key={index}>
              <div className="mb-4 flex justify-end">
                <div
                  className="rounded-lg px-4 py-3 bg-violet-100 text-gray-700"
                  style={{ maxWidth: "75%" }}
                >
                  <div className="flex flex-col">
                    <div className="mb-1 whitespace-pre-line">{exchange.question}</div>
                    <span className="text-xs self-end mt-1 text-gray-500">
                      {formatTimestamp(exchange.askedAt)}
                    </span>
                  </div>
                </div>
              </div>
              <div className="mb-4 flex justify-start">
                <div
                  className="rounded-lg px-4 py-3 bg-white border border-gray-200 text-gray-700"
                  style={{ maxWidth: "75%" }}
                >
                  <div className="flex flex-col">
                    <div className="mb-1 whitespace-pre-line leading-relaxed">
                      {exchange.text}
                    </div>
                    {exchange.sources.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-1.5">
                          Based on {exchange.sources.length}{" "}
                          {exchange.sources.length === 1 ? "entry" : "entries"}
                        </p>
                        {exchange.sources.map((source) => (
                          <div
                            key={source.uuid}
                            className="flex items-baseline gap-2 py-0.5"
                          >
                            <span className="text-xs text-gray-400 tabular-nums shrink-0">
                              {source.date}
                            </span>
                            <span className="text-xs text-gray-600 truncate">
                              <span className="font-semibold">{source.label}</span>
                              {" — "}
                              {source.snippet}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    <span className="text-xs self-end mt-1 text-gray-400">
                      {formatTimestamp(exchange.answeredAt)}
                    </span>
                  </div>
                </div>
              </div>
            </React.Fragment>
          ))}

          {pending && (
            <div className="mb-4 flex justify-end">
              <div
                className="rounded-lg px-4 py-3 bg-violet-100 text-gray-700"
                style={{ maxWidth: "75%" }}
              >
                <div className="flex flex-col">
                  <div className="mb-1 whitespace-pre-line">{pending.question}</div>
                  <span className="text-xs self-end mt-1 text-gray-500">
                    {formatTimestamp(pending.askedAt)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {status === "loading" && (
            <div className="flex items-center mb-4">
              <div className="bg-white border border-gray-200 rounded-lg px-4 py-3 flex items-center">
                <CircularProgress
                  fontSize="small"
                  className="text-violet-600 animate-spin mr-2"
                />
                <span className="text-gray-500 text-sm">
                  Searching the logbook...
                </span>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        <div className="bg-white border-t p-4">
          <div className="flex">
            <div className="flex-1 bg-gray-100 rounded-full flex items-center px-4">
              <textarea
                ref={textareaRef}
                value={question}
                disabled={!available}
                maxLength={MAX_QUESTION_CHARS}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    ask();
                  }
                }}
                placeholder='Ask about this store&apos;s logbook history, e.g. "when did we last service the fryer?"'
                className="w-full bg-transparent py-2 outline-none resize-none text-gray-700"
                rows="1"
              />
              <button
                onClick={ask}
                disabled={!canSend}
                className={`ml-2 rounded-full w-8 h-8 flex items-center justify-center ${
                  canSend ? "bg-violet-600 text-white" : "bg-gray-100 text-gray-500"
                }`}
              >
                <SendIcon fontSize="small" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
